import { useMemo } from 'react';

import {
  canEditActionItems as hasActionItemsEditAuthority,
  canUseActionItemsPermission,
} from '@/features/action-items/action-items-effective-authority.ts';
import { DEFAULT_ROLES } from '@/features/auth/permissions';
import { userHasPermission } from '@/features/auth/logic/contactLinking';
import { Permission } from '@/features/auth/types';
import { useFeedSync } from '@/features/feed/hooks/useFeedSync';
import { useAppStore } from '@/store/useAppStore';

import type { ProjectListenerId } from '../project-data-requirements';
import { useActionItemsSync } from './useActionItemsSync';
import { useAttendanceSync } from './useAttendanceSync';
import { useBlockingSync } from './useBlockingSync';
import { useCostumesSync } from './useCostumesSync';
import { useScheduleSync } from './useScheduleSync';
import { useTranscriptionSync } from './useTranscriptionSync';

export function useProjectListenersSync(input: {
  canAccessAsOrgAdmin?: boolean;
  canSyncCostumes: boolean;
  listenerIds: readonly ProjectListenerId[];
}): void {
  const listenerSet = useMemo(
    () => new Set<ProjectListenerId>(input.listenerIds),
    [input.listenerIds],
  );
  const member = useAppStore(state => state.cloud.member);
  const cloudEnabled = useAppStore(state => state.cloud.enabled);
  const roles = useAppStore(state => state.admin.roles);
  const user = useAppStore(state => state.auth.user);
  const effectiveRoles = roles.length > 0 ? roles : DEFAULT_ROLES;
  const canSyncTranscription = user?.isSuperAdmin === true ||
    userHasPermission(member, effectiveRoles, Permission.EDIT_NOTES);
  const actionItemsAuthority = {
    canAccessAsOrgAdmin: input.canAccessAsOrgAdmin,
    cloudEnabled,
    member,
    roles,
    user,
  };
  const canViewActionItems = canUseActionItemsPermission({
    ...actionItemsAuthority,
    permission: Permission.VIEW_ACTION_ITEMS,
  });
  const canSyncActionItemDraftReviews = canViewActionItems && hasActionItemsEditAuthority(actionItemsAuthority);
  const shouldSyncActionItems = listenerSet.has('actionItems') && canViewActionItems;

  useScheduleSync(listenerSet.has('schedule'));
  useAttendanceSync(listenerSet.has('attendance'));
  useFeedSync(listenerSet.has('feed'));
  useBlockingSync(listenerSet.has('blocking'));
  useActionItemsSync(shouldSyncActionItems, canSyncActionItemDraftReviews ? 'edit' : 'view');
  useTranscriptionSync(canSyncTranscription && listenerSet.has('transcription'));
  useCostumesSync(input.canSyncCostumes && listenerSet.has('costumes'));
}
